/**
 * section-o16-find-us.js
 * Responsibilities:
 *   1. Golden-angle bloom declustering for densely-overlapping map pins.
 *   2. Country filter dropdown — show/hide partner cards and category headings.
 *   3. Overflow toggles — expand/collapse the "View all" partner grid.
 *   4. Nearest-partner city search — re-sort the partner grid by distance from
 *      a typed city, resolved against a first-party gazetteer (no geolocation,
 *      no external geocoding APIs).
 * Progressive enhancement throughout — page is fully functional without JS.
 */
(function () {
  'use strict';

  var GOLDEN_ANGLE = 137.508 * Math.PI / 180;

  // Thresholds in SVG 1200×600 space (matching the original implementation)
  function getThresholds() {
    var mobile = window.innerWidth < 750;
    return { MIN_DIST: mobile ? 3 : 5, BASE_SCATTER: mobile ? 6 : 12 };
  }

  function initBloom(pinsEl) {
    var pins = Array.prototype.slice.call(pinsEl.querySelectorAll('.o16-map__pin'));
    if (!pins.length) return;

    var t = getThresholds();
    var MIN_DIST = t.MIN_DIST;
    var BASE_SCATTER = t.BASE_SCATTER;

    // Convert CSS % back to 1200×600 SVG space for distance math
    var points = pins.map(function (pin) {
      return {
        el: pin,
        // data-x/data-y are the server-rendered true percent positions
        x: parseFloat(pin.dataset.x) * 12,
        y: parseFloat(pin.dataset.y) * 6,
        dispX: parseFloat(pin.dataset.x) * 12,
        dispY: parseFloat(pin.dataset.y) * 6
      };
    });

    // scatterRadius scales with BASE_SCATTER (zoom=1 always here)
    var scatterRadius = BASE_SCATTER * ((3.5 - 1) / 2.5);

    var used = new Array(points.length);

    for (var i = 0; i < points.length; i++) {
      if (used[i]) continue;
      var group = [i];
      used[i] = true;

      for (var j = i + 1; j < points.length; j++) {
        if (used[j]) continue;
        var dx = points[i].x - points[j].x;
        var dy = points[i].y - points[j].y;
        if (Math.sqrt(dx * dx + dy * dy) < MIN_DIST) {
          group.push(j);
          used[j] = true;
        }
      }

      if (group.length > 1 && scatterRadius > 0.5) {
        // Centroid
        var cx = 0, cy = 0;
        for (var k = 0; k < group.length; k++) {
          cx += points[group[k]].x;
          cy += points[group[k]].y;
        }
        cx /= group.length;
        cy /= group.length;

        var step = scatterRadius / Math.sqrt(group.length);

        for (var k = 0; k < group.length; k++) {
          if (k === 0) {
            points[group[k]].dispX = cx;
            points[group[k]].dispY = cy;
          } else {
            var angle = k * GOLDEN_ANGLE;
            var r = Math.sqrt(k) * step;
            points[group[k]].dispX = cx + Math.cos(angle) * r;
            points[group[k]].dispY = cy + Math.sin(angle) * r;
          }
        }
      }
    }

    // Write back to CSS %
    points.forEach(function (p) {
      p.el.style.left = (p.dispX / 12).toFixed(3) + '%';
      p.el.style.top  = (p.dispY / 6).toFixed(3) + '%';
    });
  }

  function run() {
    var containers = document.querySelectorAll('.o16-map__pins');
    containers.forEach(function (c) { initBloom(c); });
  }

  // Debounced resize — re-bloom across the 750px mobile/desktop boundary
  var resizeTimer;
  var lastMobile = window.innerWidth < 750;

  window.addEventListener('resize', function () {
    clearTimeout(resizeTimer);
    resizeTimer = setTimeout(function () {
      var nowMobile = window.innerWidth < 750;
      if (nowMobile !== lastMobile) {
        lastMobile = nowMobile;
        // Reset to true positions before re-blooming
        var pins = document.querySelectorAll('.o16-map__pin');
        pins.forEach(function (p) {
          p.style.left = p.dataset.x + '%';
          p.style.top  = p.dataset.y + '%';
        });
        run();
      }
    }, 200);
  });

  // ── Overflow toggles ───────────────────────────────────────────────────────
  // Each [data-overflow-toggle] button expands its parent .o16-partners__category
  // via the class is-expanded, which reveals .o16-partners__card--overflow cards.
  function initOverflowToggles() {
    var buttons = document.querySelectorAll('[data-overflow-toggle]');
    buttons.forEach(function (btn) {
      btn.addEventListener('click', function () {
        var cat = btn.closest('.o16-partners__category');
        if (!cat) return;
        var expanded = cat.classList.toggle('is-expanded');
        btn.setAttribute('aria-expanded', expanded ? 'true' : 'false');
      });
    });
  }

  // ── Country filter + nearest-city search: shared expanded-state sync ───────
  // Scope: affects ONLY the [data-partners-category] div (Our Partners).
  // Restaurants and Coming Soon are never touched.
  //
  // The "Our Partners" grid auto-expands (revealing overflow cards + hiding the
  // toggle button) whenever a country is chosen OR a nearest-city search has
  // resolved — either signal alone, or both together, keep it expanded.
  function isCountryFilterActive(partnersEl) {
    var select = partnersEl.querySelector('[data-country-filter]');
    return !!(select && select.value);
  }

  function isNearestSearchActive(partnersEl) {
    var wrap = partnersEl.querySelector('[data-nearest-search]');
    return !!(wrap && wrap.hasAttribute('data-nearest-resolved'));
  }

  function syncExpandedState(partnersCat, partnersEl) {
    var countryActive = isCountryFilterActive(partnersEl);
    var nearestActive = isNearestSearchActive(partnersEl);

    if (countryActive || nearestActive) {
      partnersCat.classList.add('is-expanded');
    } else {
      partnersCat.classList.remove('is-expanded');
      var toggleBtn = partnersCat.querySelector('[data-overflow-toggle]');
      if (toggleBtn) toggleBtn.setAttribute('aria-expanded', 'false');
    }

    if (countryActive) {
      partnersCat.setAttribute('data-filter-active', '');
    } else {
      partnersCat.removeAttribute('data-filter-active');
    }

    if (nearestActive) {
      partnersCat.setAttribute('data-nearest-active', '');
    } else {
      partnersCat.removeAttribute('data-nearest-active');
    }
  }

  // ── Country filter ─────────────────────────────────────────────────────────
  function initCountryFilter() {
    var selects = document.querySelectorAll('[data-country-filter]');
    selects.forEach(function (select) {
      // Walk up to the partners wrapper, then find the partners-only category
      var partnersEl = select.closest('.o16-partners');
      if (!partnersEl) return;

      // The Our Partners category carries data-partners-category
      var partnersCat = partnersEl.querySelector('[data-partners-category]');
      if (!partnersCat) return;

      select.addEventListener('change', function () {
        var chosen = select.value; // '' = all countries

        // Show/hide cards inside the partners category only.
        // Inline display:none wins over .is-expanded .--overflow{display:flex}
        // so non-matching overflow cards stay hidden even when expanded.
        var cards = partnersCat.querySelectorAll('.o16-partners__card');
        cards.forEach(function (card) {
          if (!chosen || card.dataset.country === chosen) {
            card.style.display = '';
          } else {
            card.style.display = 'none';
          }
        });

        syncExpandedState(partnersCat, partnersEl);
      });
    });
  }

  // ── Nearest-partner city search ─────────────────────────────────────────────
  // CITY TEXTBOX ONLY — no browser geolocation, no external IP/geocoding.
  // Resolves the typed city against a first-party gazetteer:
  //   1. This section's own place-block cities (inline <script type="application/json">,
  //      available immediately, always accurate for this store's own data).
  //   2. The bundled assets/o16-city-coords.json "cities" map (fetched once,
  //      merged in — fills gaps for cities typed by a visitor that aren't tied
  //      to any partner).
  // Distance uses the haversine formula against each card's data-lat/data-lng.
  //
  // Sort coordinate per card, in priority order:
  //   (a) the card's own data-lat/data-lng — exact per-partner coordinates.
  //   (b) the card's data-country resolved against the bundled
  //       o16-city-coords.json "countries" centroid map — used for partners
  //       that are intentionally coordless (owner: no per-partner pin, to
  //       avoid map pin-clustering) so they still rank with their region
  //       instead of falling to the very end.
  //   (c) no coordinate and no known country centroid — sorts to the end,
  //       preserving original authoring order among ties.
  // Country-centroid matches never show a "~N km" badge (that distance is a
  // regional approximation, not the partner's real location).
  // Combines with the country filter: reordering never changes which cards
  // are display:none.

  function normalizeCityKey(str) {
    return (str || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // strip diacritics (combining marks)
      .replace(/\s+/g, ' ')
      .trim();
  }

  function haversineKm(lat1, lng1, lat2, lng2) {
    var R = 6371; // Earth radius, km
    var toRad = Math.PI / 180;
    var dLat = (lat2 - lat1) * toRad;
    var dLng = (lng2 - lng1) * toRad;
    var a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(lat1 * toRad) * Math.cos(lat2 * toRad) *
      Math.sin(dLng / 2) * Math.sin(dLng / 2);
    var c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    return R * c;
  }

  function mergeGazetteer(target, source) {
    if (!source) return;
    Object.keys(source).forEach(function (rawKey) {
      var key = normalizeCityKey(rawKey);
      var coord = source[rawKey];
      if (key && !(key in target) && Array.isArray(coord) && coord.length === 2) {
        target[key] = [parseFloat(coord[0]), parseFloat(coord[1])];
      }
    });
  }

  // Country centroids are keyed by the EXACT country strings used in the
  // "place" block's country select (case-sensitive, no normalization) since
  // they're matched directly against each card's data-country attribute.
  function mergeCountryCentroids(target, source) {
    if (!source) return;
    Object.keys(source).forEach(function (key) {
      var coord = source[key];
      if (key && !(key in target) && Array.isArray(coord) && coord.length === 2) {
        target[key] = [parseFloat(coord[0]), parseFloat(coord[1])];
      }
    });
  }

  function initNearestSearch() {
    var wraps = document.querySelectorAll('[data-nearest-search]');
    wraps.forEach(function (wrap) {
      var input = wrap.querySelector('[data-nearest-input]');
      if (!input) return;

      var partnersEl = wrap.closest('.o16-partners');
      if (!partnersEl) return;
      var partnersCat = partnersEl.querySelector('[data-partners-category]');
      if (!partnersCat) return;
      var grid = partnersCat.querySelector('.o16-partners__grid');
      if (!grid) return;

      var gazetteer = {};
      var countryCentroids = {};

      // 1. Inline section gazetteer (own place cities) — available immediately.
      var inlineScript = partnersEl.querySelector('[data-o16-city-gazetteer]');
      if (inlineScript) {
        try {
          mergeGazetteer(gazetteer, JSON.parse(inlineScript.textContent));
        } catch (e) {
          // Malformed inline gazetteer — bundled asset (below) still applies.
        }
      }

      // 2. Bundled first-party gazetteer asset — fetched once, merged when ready.
      //    Shape: { "cities": { "London": [lat,lng], ... }, "countries": { "United Kingdom": [lat,lng], ... } }
      var assetUrl = input.getAttribute('data-gazetteer-asset');
      if (assetUrl) {
        fetch(assetUrl)
          .then(function (res) { return res.ok ? res.json() : null; })
          .then(function (data) {
            if (data) {
              mergeGazetteer(gazetteer, data.cities);
              mergeCountryCentroids(countryCentroids, data.countries);
            }
            // Retry resolution in case the visitor typed a city before the
            // bundled gazetteer finished loading.
            if (input.value.trim()) resolveAndSort();
          })
          .catch(function () {
            // Offline / blocked request — inline gazetteer still works.
          });
      }

      function resetOrder() {
        var cards = Array.prototype.slice.call(grid.querySelectorAll('.o16-partners__card'));
        cards.sort(function (a, b) {
          return (parseInt(a.dataset.order, 10) || 0) - (parseInt(b.dataset.order, 10) || 0);
        });
        cards.forEach(function (card) {
          grid.appendChild(card);
          var badge = card.querySelector('[data-nearest-distance]');
          if (badge) badge.remove();
        });
      }

      function applySort(coord) {
        var cards = Array.prototype.slice.call(grid.querySelectorAll('.o16-partners__card'));
        var ranked = cards.map(function (card) {
          var lat = parseFloat(card.dataset.lat);
          var lng = parseFloat(card.dataset.lng);
          var hasExactCoords = !isNaN(lat) && !isNaN(lng);
          var sortLat = lat;
          var sortLng = lng;
          var isApprox = false;

          if (!hasExactCoords) {
            // No per-partner pin (intentional — avoids map pin-clustering).
            // Fall back to the partner's country centroid so it still ranks
            // with its region instead of dropping to the very end.
            var centroid = countryCentroids[card.dataset.country];
            if (centroid) {
              sortLat = centroid[0];
              sortLng = centroid[1];
              isApprox = true;
            }
          }

          var hasCoords = hasExactCoords || isApprox;
          return {
            card: card,
            hasCoords: hasCoords,
            isApprox: isApprox,
            dist: hasCoords ? haversineKm(coord[0], coord[1], sortLat, sortLng) : Infinity
          };
        });

        ranked.sort(function (a, b) {
          if (a.hasCoords && b.hasCoords) return a.dist - b.dist;
          if (a.hasCoords !== b.hasCoords) return a.hasCoords ? -1 : 1;
          // Neither has coordinates nor a country centroid: preserve original authoring order.
          return (parseInt(a.card.dataset.order, 10) || 0) - (parseInt(b.card.dataset.order, 10) || 0);
        });

        ranked.forEach(function (entry) {
          grid.appendChild(entry.card);
          var existing = entry.card.querySelector('[data-nearest-distance]');
          if (existing) existing.remove();
          // Only show a "~N km" badge for exact per-partner coordinates —
          // a country-centroid distance is a regional approximation, not
          // this partner's real location, so it stays unlabeled.
          if (entry.hasCoords && !entry.isApprox) {
            var badge = document.createElement('p');
            badge.className = 'o16-partners__distance';
            badge.setAttribute('data-nearest-distance', '');
            badge.textContent = '~' + Math.round(entry.dist) + ' km';
            entry.card.appendChild(badge);
          }
        });
      }

      function resolveAndSort() {
        var key = normalizeCityKey(input.value);

        if (!key) {
          resetOrder();
          wrap.removeAttribute('data-nearest-resolved');
          syncExpandedState(partnersCat, partnersEl);
          return;
        }

        var coord = gazetteer[key];
        if (!coord) {
          // Unknown city, or the bundled gazetteer hasn't loaded yet — leave the
          // current sort/expanded state untouched (avoids flicker mid-typing).
          return;
        }

        applySort(coord);
        wrap.setAttribute('data-nearest-resolved', '');
        syncExpandedState(partnersCat, partnersEl);
      }

      var debounceTimer;
      input.addEventListener('input', function () {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(resolveAndSort, 250);
      });
      // Native "clear" (x) button on type="search" fires a 'search' event.
      input.addEventListener('search', function () {
        clearTimeout(debounceTimer);
        resolveAndSort();
      });
      input.addEventListener('keydown', function (e) {
        if (e.key === 'Enter') {
          e.preventDefault();
          clearTimeout(debounceTimer);
          resolveAndSort();
        }
      });
      input.addEventListener('change', resolveAndSort);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      run();
      initOverflowToggles();
      initCountryFilter();
      initNearestSearch();
    });
  } else {
    run();
    initOverflowToggles();
    initCountryFilter();
    initNearestSearch();
  }
})();
